<template>
    <div class="voice-agent-call-message" :style="messageColors">
        <div class="call-content" @click="showDetails" style="cursor: pointer">
            <div class="call-icon">
                <i class="material-icons">{{ getCallIcon() }}</i>
            </div>
            <div class="call-info">
                <div class="call-title">{{ getCallTitle() }}</div>
                <div class="call-status-row">
                    <a-tag :color="getStatusColor(callData.call_status)">{{ callData.call_status }}</a-tag>
                    <span class="call-duration" v-if="callData.start_timestamp">
                        {{ formatDuration(callData.start_timestamp, callData.end_timestamp) }}
                    </span>
                </div>
                <div class="call-number-row">
                    <div class="call-from">
                        <span class="call-label">{{ $t("auto.caller") }}:</span>
                        <span class="call-value">{{ callData.from_number || $t("auto.unknown") }}</span>
                    </div>
                    <div class="call-to">
                        <span class="call-label">{{ $t("auto.callee") }}:</span>
                        <span class="call-value">{{ callData.to_number || $t("auto.unknown") }}</span>
                    </div>
                </div>
                <div class="call-actions" @click.stop>
                    <a-button type="link" size="small" v-if="hasDetails()" @click="showDetails">
                        <div class="d-flex gap-5 align-items-center">
                            <i class="material-icons">info</i>
                            {{ $t("auto.showCallDetails") }}
                        </div>
                    </a-button>

                    <a-button type="link" size="small" v-if="hasAudioRecording()" @click="togglePlayAudio">
                        <div class="d-flex gap-5 align-items-center">
                            <i class="material-icons">{{ isPlaying ? "pause" : "play_arrow" }}</i>
                            {{ isPlaying ? $t("auto.stopVoiceRecording") : $t("auto.playVoiceRecording") }}
                        </div>
                    </a-button>
                </div>
            </div>
        </div>

        <!-- Mini Audio Player (görünmez) -->
        <audio ref="miniAudioPlayer" :src="callData.recording_url" style="display: none"></audio>

        <!-- Detay Modali -->
        <a-modal
            v-model="detailsModalVisible"
            :title="$t('auto.callDetails')"
            width="800px"
            :footer="null"
            @cancel="closeModal"
        >
            <div v-if="callData" class="call-details">
                <div class="call-info-details">
                    <div class="call-info-item"><strong>{{ $t("auto.labelCallId") }}</strong> {{ callData.call_id }}</div>
                    <div class="call-info-item"><strong>{{ $t("auto.timestampLabel") }}</strong> {{ formatDate(callData.start_timestamp) }}</div>
                    <div class="call-info-item">
                        <strong>{{ $t("auto.durationLabel") }}</strong> {{ formatDuration(callData.start_timestamp, callData.end_timestamp) }}
                    </div>
                    <div class="call-info-item"><strong>{{ $t("auto.caller") }}:</strong> {{ callData.from_number }}</div>
                    <div class="call-info-item"><strong>{{ $t("auto.callee") }}:</strong> {{ callData.to_number }}</div>
                    <div class="call-info-item">
                        <strong>{{ $t("auto.statusLabel") }}:</strong>
                        <a-tag :color="getStatusColor(callData.call_status)">{{ callData.call_status }}</a-tag>
                    </div>
                    <div class="call-info-item" v-if="callData.disconnection_reason">
                        <strong>{{ $t("auto.labelDisconnectionReason") }}</strong>
                        <a-tag :color="getDisconnectionReasonColor(callData.disconnection_reason)">
                            {{ formatDisconnectionReason(callData.disconnection_reason) }}
                        </a-tag>
                    </div>
                    <div class="call-info-item" v-if="getCallCost()">
                        <strong>{{ $t("auto.costLabel") }}</strong> {{ getCallCost() }} ₺
                    </div>
                </div>

                <div v-if="hasTranscript()" class="transcript-container">
                    <h3>{{ $t("auto.conversationTextHeading") }}</h3>
                    <div class="transcript-content">
                        <div
                            v-for="(line, index) in getTranscriptLines()"
                            :key="index"
                            class="transcript-line"
                            @click="seekToTranscriptLine(index)"
                        >
                            {{ line }}
                        </div>
                    </div>
                </div>

                <div v-if="hasCallSummary()" class="call-summary">
                    <h3>{{ $t("auto.callSummaryHeading") }}</h3>
                    <p>{{ callData.call_analysis.call_summary }}</p>
                </div>

                <div v-if="hasAudioRecording()" class="audio-player-container">
                    <h3>{{ $t("auto.audioRecordingHeading") }}</h3>
                    <div class="modern-audio-player">
                        <div class="audio-player-info">
                            <i class="material-icons audio-icon pulse">music_note</i>
                            <div class="audio-title-container">
                                <div class="audio-title">{{ formatDate(callData.start_timestamp) }}</div>
                            </div>
                        </div>
                        <div class="audio-player-wrapper">
                            <audio controls :src="callData.recording_url" style="width: 100%" ref="audioPlayer"></audio>
                        </div>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";

export default {
    name: "VoiceAgentCallMessage",
    props: {
        message: {
            type: Object,
            required: true,
        },
        messageColors: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            detailsModalVisible: false,
            transcriptTimestamps: [],
            isPlaying: false,
        };
    },
    computed: {
        callData() {
            return _.get(this.message, "raw.callLog", {});
        },
    },
    watch: {
        detailsModalVisible(newVal) {
            // Modal kapandığında ses oynatıcısını durdur
            if (!newVal && this.$refs.audioPlayer) {
                this.$refs.audioPlayer.pause();
            }

            // Modal kapandığında mini player'ı da durdur
            if (!newVal && this.$refs.miniAudioPlayer) {
                this.$refs.miniAudioPlayer.pause();
                this.isPlaying = false;
            }
        },
    },
    methods: {
        getCallIcon() {
            const status = this.callData.call_status;

            switch (status) {
                case "ended":
                    return "call_end";
                case "error":
                    return "call_missed";
                case "ongoing":
                    return "call_active";
                case "registered":
                default:
                    return "call";
            }
        },

        getCallTitle() {
            const callType = this.callData.call_type;
            const direction = this.callData.direction;

            if (callType === "web_call") {
                return this.$t("auto.webCallTitle");
            } else {
                if (direction === "inbound") {
                    return this.$t("auto.inboundPhoneCallTitle");
                } else {
                    return this.$t("auto.outboundPhoneCallTitle");
                }
            }
        },

        getStatusColor(status) {
            const colorMap = {
                ended: "green",
                error: "red",
                ongoing: "blue",
                registered: "orange",
            };

            return colorMap[status] || "default";
        },

        getDisconnectionReasonColor(reason) {
            const colorMap = {
                user_hangup: "green",
                agent_hangup: "blue",
                dial_failed: "red",
                dial_no_answer: "orange",
            };

            return colorMap[reason] || "default";
        },

        formatDisconnectionReason(reason) {
            if (!reason) return "-";
            return reason.replace(/_/g, " ");
        },

        formatDate(timestamp) {
            if (!timestamp) return "-";
            return moment(timestamp).format("DD/MM/YYYY HH:mm");
        },

        formatDuration(startTimestamp, endTimestamp) {
            if (!startTimestamp) return "0:00";

            const end = endTimestamp ? moment(endTimestamp) : moment();
            const duration = moment.duration(end.diff(moment(startTimestamp)));
            const minutes = Math.floor(duration.asMinutes());
            const seconds = Math.floor(duration.asSeconds() % 60);

            return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
        },

        getCallCost() {
            const cost = _.get(this.callData, "call_cost.total_supsis_cost_as_tl", 0);
            return cost.toFixed(2);
        },

        hasDetails() {
            return this.hasTranscript() || this.hasCallSummary() || this.hasAudioRecording();
        },

        hasTranscript() {
            return !!this.callData.transcript;
        },

        getTranscriptLines() {
            if (!this.callData.transcript) return [];
            return this.callData.transcript.split("\n");
        },

        hasCallSummary() {
            return !!_.get(this.callData, "call_analysis.call_summary");
        },

        hasAudioRecording() {
            return !!this.callData.recording_url;
        },

        togglePlayAudio() {
            const player = this.$refs.miniAudioPlayer;
            if (!player) return;

            if (this.isPlaying) {
                player.pause();
                this.isPlaying = false;
            } else {
                // Ses oynatıcı başlattığında detay modalının içindeki oynatıcıyı durdur
                if (this.$refs.audioPlayer) {
                    this.$refs.audioPlayer.pause();
                }

                player.play().catch((error) => {
                    console.error("Ses kaydı oynatılırken bir hata oluştu:", error);
                    this.$message.error("Ses kaydı oynatılamadı");
                });
                this.isPlaying = true;
            }
        },

        closeModal() {
            // Modal kapanırken ses oynatıcıyı durdur
            if (this.$refs.audioPlayer) {
                this.$refs.audioPlayer.pause();
            }

            // Mini player'ı da durdur
            if (this.$refs.miniAudioPlayer) {
                this.$refs.miniAudioPlayer.pause();
                this.isPlaying = false;
            }

            this.detailsModalVisible = false;
        },

        seekToTranscriptLine(index) {
            if (this.hasAudioRecording() && this.$refs.audioPlayer) {
                // Timestamp'leri hazırla
                if (this.transcriptTimestamps.length === 0) {
                    this.prepareTranscriptTimestamps();
                }

                // İlgili satırın timestamp'i varsa, o zaman dilimini oynat
                if (this.transcriptTimestamps[index]) {
                    const timeToSeek = this.transcriptTimestamps[index];
                    this.$refs.audioPlayer.currentTime = timeToSeek;
                    this.$refs.audioPlayer.play();
                }
            }
        },

        prepareTranscriptTimestamps() {
            // Transcript_object verisinden timestamp bilgilerini hazırla
            if (this.callData.transcript_object) {
                this.callData.transcript_object.forEach((entry, entryIndex) => {
                    if (entry.words && entry.words.length > 0) {
                        // Her bir mesaj için başlangıç zamanını kullan
                        this.transcriptTimestamps[entryIndex] = entry.words[0].start;
                    }
                });
            }
        },

        showDetails() {
            this.detailsModalVisible = true;
            // Modal açıldığında transcript timestamp'leri hazırla
            this.$nextTick(() => {
                this.prepareTranscriptTimestamps();
            });
        },
    },
    mounted() {
        // Mini audio player olaylarını dinle
        if (this.$refs.miniAudioPlayer) {
            this.$refs.miniAudioPlayer.addEventListener("ended", () => {
                this.isPlaying = false;
            });
        }
    },
    beforeDestroy() {
        // Component yok edilmeden önce ses oynatıcıyı durdur
        const player = this.$refs.miniAudioPlayer;
        if (player) {
            player.pause();
            this.isPlaying = false;
        }
    },
};
</script>

<style scoped lang="scss">
.voice-agent-call-message {
    padding: 10px;
    border-radius: 8px;
    margin: 5px 0;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);

    .call-content {
        display: flex;
        align-items: flex-start;

        .call-icon {
            margin-right: 12px;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.2);
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                font-size: 24px;
            }
        }

        .call-info {
            flex: 1;

            .call-title {
                font-weight: 600;
                font-size: 16px;
                margin-bottom: 8px;
            }

            .call-status-row {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-bottom: 8px;

                .call-duration {
                    font-size: 14px;
                    opacity: 0.8;
                }
            }

            .call-number-row {
                display: flex;
                flex-direction: column;
                gap: 5px;
                font-size: 13px;
                margin-bottom: 8px;

                .call-from,
                .call-to {
                    display: flex;
                    align-items: center;
                    gap: 5px;

                    .call-label {
                        font-weight: 500;
                        min-width: 60px;
                    }

                    .call-value {
                        font-family: monospace;
                    }
                }
            }

            .call-actions {
                display: flex;
                gap: 10px;
                margin-top: 10px;
            }
        }
    }
}

.call-details {
    .call-info-details {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
        margin-bottom: 20px;

        .call-info-item {
            padding: 5px 0;
        }
    }

    .transcript-container {
        margin-top: 20px;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 15px;
        background-color: #f9f9f9;

        h3 {
            margin-top: 0;
            margin-bottom: 10px;
        }

        .transcript-content {
            max-height: 300px;
            overflow-y: auto;

            .transcript-line {
                padding: 5px 0;
                border-bottom: 1px solid #f0f0f0;
                cursor: pointer;
                transition: background-color 0.2s;

                &:hover {
                    background-color: rgba(58, 123, 213, 0.1);
                }

                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }

    .call-summary {
        margin-top: 20px;

        h3 {
            margin-top: 0;
            margin-bottom: 10px;
        }
    }

    .audio-player-container {
        margin-top: 20px;

        h3 {
            margin-top: 0;
            margin-bottom: 10px;
        }
    }
}

.modern-audio-player {
    display: flex;
    flex-direction: column;
    background: rgb(255 229 224);
    border-radius: 8px;
    padding: 12px;
    margin-top: 10px;

    .audio-player-info {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 10px;

        .audio-icon {
            font-size: 22px;
            color: #3a7bd5;
            background: white;
            border-radius: 50%;
            padding: 6px;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
            animation: pulse 2s infinite;
        }

        .audio-title-container {
            .audio-title {
                font-weight: 500;
                color: #333;
                font-size: 14px;
            }
        }
    }

    .audio-player-wrapper {
        audio {
            border-radius: 20px;
            height: 36px;
        }
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    }
    50% {
        transform: scale(1.05);
        box-shadow: 0 6px 15px rgba(58, 123, 213, 0.4);
    }
    100% {
        transform: scale(1);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    }
}
</style>
